import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Layout } from 'react-grid-layout';
import { getPreferences, putPreferences } from '../../api/preferences';
import {
  DEFAULT_STATE,
  isDashboardState,
  normaliseSpec,
  type DashboardState,
  type WidgetSpec,
} from '../../dashboard/spec';
import { useEnvironment } from '../useEnvironment/useEnvironment';

const SAVE_DELAY_MS = 800;

/**
 * The staff member's dashboard: layout plus one spec per widget. Loaded from the API once, saved back a moment after
 * every change, so the next login on any device sees the same board.
 */
export const useDashboardState = () => {
  const { environment } = useEnvironment();
  const prefs = useQuery({
    queryKey: ['preferences'],
    queryFn: () => getPreferences(environment),
    staleTime: Infinity,
  });
  const [state, setState] = useState<DashboardState | null>(null);
  const [saving, setSaving] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dirty = useRef(false);

  useEffect(() => {
    if (prefs.isSuccess && state === null) {
      const stored = prefs.data?.dashboard;
      setState(isDashboardState(stored) ? stored : DEFAULT_STATE);
    }
    if (prefs.isError && state === null) {
      setState(DEFAULT_STATE);
    }
  }, [prefs.isSuccess, prefs.isError, prefs.data, state]);

  useEffect(() => {
    if (!state || !dirty.current) {
      return undefined;
    }
    if (timer.current) {
      clearTimeout(timer.current);
    }
    timer.current = setTimeout(() => {
      setSaving(true);
      putPreferences(environment, state)
        .catch(() => undefined)
        .finally(() => setSaving(false));
    }, SAVE_DELAY_MS);
    return () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
    };
  }, [state, environment]);

  const update = useCallback((updater: (current: DashboardState) => DashboardState) => {
    dirty.current = true;
    setState((current) => (current ? updater(current) : current));
  }, []);

  const setLayout = useCallback(
    (layout: Layout[]) =>
      update((current) => ({
        ...current,
        layout: layout.map(({ i, x, y, w, h, minW, minH }) => ({ i, x, y, w, h, minW, minH })),
      })),
    [update],
  );

  const updateWidget = useCallback(
    (id: string, patch: Partial<WidgetSpec>) =>
      update((current) => ({
        ...current,
        widgets: current.widgets.map((widget) =>
          widget.id === id ? normaliseSpec({ ...widget, ...patch }) : widget,
        ),
      })),
    [update],
  );

  const removeWidget = useCallback(
    (id: string) =>
      update((current) => ({
        ...current,
        widgets: current.widgets.filter((widget) => widget.id !== id),
        layout: current.layout.filter((item) => item.i !== id),
      })),
    [update],
  );

  const addWidget = useCallback(
    (widget: WidgetSpec, size: { w: number; h: number; minW?: number; minH?: number }) =>
      update((current) => {
        const bottom = current.layout.reduce((max, { y, h }) => Math.max(max, y + h), 0);
        return {
          ...current,
          widgets: [...current.widgets, widget],
          layout: [...current.layout, { i: widget.id, x: 0, y: bottom, ...size }],
        };
      }),
    [update],
  );

  const reset = useCallback(() => update(() => DEFAULT_STATE), [update]);

  return {
    state,
    loading: state === null,
    saving,
    setLayout,
    updateWidget,
    removeWidget,
    addWidget,
    reset,
  };
};
